import { useRef } from 'react';

import {
  Box,
  type CSSObject,
  Group,
  Image,
  type MantineTheme,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';

import { IconDownload } from '@tabler/icons-react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import TengoLogoWithText from '../../shared/assets/UI/logo/logo_with_text_dark.svg';
import TengoLogoWithoutText from '../../shared/assets/UI/logo/logo_without_text.svg';
import { Button } from '../../shared/components/UI/Button/Button';
import { BuyerLogo } from '../../shared/components/UI/BuyerLogo/BuyerLogo';
import { Loader } from '../../shared/components/UI/Loader/Loader';
import { NAVBAR_HEIGHT } from '../../shared/components/UI/Navbar/Navbar';
import TenderStatusBadge from '../../shared/components/UI/TenderStatusBadge/TenderStatusBadge';
import { Tooltip } from '../../shared/components/UI/Tooltip/Tooltip';

import { LocationComponent } from '../../features/notice/components/InfoCards/DataCard';
import { AnswerText } from '../../features/notice/components/summarySheet/components/AnswerElement';
import { AnswerLeftBorder } from '../../features/notice/components/summarySheet/components/AnswerLeftBorder';
import { getAnswerType } from '../../features/notice/components/summarySheet/utils/getAnswerType';
import {
  InfoCard,
  InfoCardText,
} from '../../features/sharedSummarySheet/components/InfoCard';
import { useSummarySheetShareQuery } from '../../features/sharedSummarySheet/summarySheetShare.hook';
import type { GetSummarySheetShareDTO } from '../../shared/api/magellan/summarySheet/dto';
import { CPVTooltipContent } from '../../shared/components/CPVRow';
import { TenderCategoryMap } from '../../shared/entities/Category';
import type { SummaryCriterionAnswerWithNumberOfComments } from '../../shared/entities/SummaryCriterionAnswer';
import { formatCurrency, formatDurationValue } from '../../shared/utils/format';
import PageNotFound from './PageNotFound';

export default function SummarySheetSharePage() {
  const { uuid } = useParams();
  if (!uuid) {
    throw new Error('Missing UUID to access shared summary sheet');
  }
  const { t } = useTranslation('shared');
  const summarySheetShareQuery = useSummarySheetShareQuery(uuid);
  const contentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle: `${t('export.title')} - ${summarySheetShareQuery.data?.buyerName} - ${uuid}`,
  });

  if (summarySheetShareQuery.isLoading) {
    return (
      <Group position="center" align="center" h="100vh">
        <Loader />
      </Group>
    );
  }

  const summarySheet = summarySheetShareQuery.data;

  if (!summarySheet) {
    return <PageNotFound />;
  }

  return (
    <Stack spacing={0} ref={contentRef}>
      <Header handlePrint={handlePrint} />
      <Box
        px="03"
        sx={theme => ({
          background: theme.other.gradients.backgroundLight,
        })}
      >
        <Box maw="1280px" mx="auto" mb="05">
          <Group spacing="02" position="right" align="center" my="03">
            <Box
              w="03"
              h="03"
              sx={theme => ({
                borderRadius: theme.radius.lg,
                background: theme.other.gradients.aiHorizontal,
              })}
            />
            <Text
              variant="sm"
              fw={400}
              c="transparent"
              sx={theme => ({
                background: theme.other.gradients.aiHorizontal,
                backgroundClip: 'text',
              })}
            >
              {t('headerAiExplanation')}
            </Text>
          </Group>
          <SummarySheetContent summarySheet={summarySheet} />
        </Box>
      </Box>
    </Stack>
  );
}

function SummarySheetContent({
  summarySheet,
}: {
  summarySheet: GetSummarySheetShareDTO;
}) {
  const { t } = useTranslation('shared');

  return (
    <Stack spacing="05">
      <Paper shadow="sm" radius="md" p="05" sx={paperBorder}>
        <Group spacing="02" noWrap>
          <BuyerLogo
            size="sm"
            radius="sm"
            logoURL={summarySheet.buyerLogoURL}
          />
          <Text truncate variant="sm" fw={400} c="primary.6">
            {summarySheet.buyerName}
          </Text>
        </Group>
        <Title mt="02" order={5} c="gray.9" lineClamp={3} tt="capitalize">
          {summarySheet.tenderTitle}
        </Title>
      </Paper>

      <Paper radius="md" p="05" sx={paperBorder}>
        <Group spacing="03" align="stretch">
          <InfoCard label="Statut">
            <TenderStatusBadge status={summarySheet.status} />
          </InfoCard>

          <InfoCard label="Montant">
            <InfoCardText
              isLlmEnriched={summarySheet.isEstimatedValueInEurLLmEnriched}
            >
              {summarySheet.estimatedValueInEur
                ? `${formatCurrency(summarySheet.estimatedValueInEur)} HT`
                : '-'}
            </InfoCardText>
          </InfoCard>

          <InfoCard label="Durée">
            <InfoCardText
              isLlmEnriched={summarySheet.isDurationInMonthLLmEnriched}
            >
              {formatDurationValue(summarySheet.durationInMonth) ?? '-'}
            </InfoCardText>
          </InfoCard>

          <InfoCard label="Lieu d'exécution">
            <InfoCardText>
              <LocationComponent location={summarySheet.executionLocation} />
            </InfoCardText>
          </InfoCard>

          <InfoCard label="Nombre de lots">
            <InfoCardText>{summarySheet.lotCount || 'Non alotti'}</InfoCardText>
          </InfoCard>

          <InfoCard label="Type de marché">
            <InfoCardText>
              {TenderCategoryMap.get(summarySheet.category)}
            </InfoCardText>
          </InfoCard>

          <InfoCard label="Réf. acheteur">
            <InfoCardText>
              {summarySheet.buyerInternalReference ?? '-'}
            </InfoCardText>
          </InfoCard>

          <InfoCard label="CPV">
            <Tooltip content={<CPVTooltipContent cpvs={summarySheet.cpvs} />}>
              <Text variant="sm" truncate fw={400} c="gray.9">
                {summarySheet.cpvs.map(cpv => cpv.title).join(', ')}
              </Text>
            </Tooltip>
          </InfoCard>
        </Group>
      </Paper>

      <Paper shadow="sm" radius="md" p="05" sx={paperBorder}>
        <Stack spacing="04">
          <Title order={5} c="gray.9">
            {t('criteriaCard.title')}
          </Title>
          <Stack spacing="02">
            {summarySheet.criteriaAnswers.map(criterion => (
              <Criterion key={criterion.id} criterion={criterion} />
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Footer />
    </Stack>
  );
}

function Criterion({
  criterion,
}: {
  criterion: SummaryCriterionAnswerWithNumberOfComments;
}) {
  const answerType = getAnswerType(criterion);
  const { t } = useTranslation('shared');
  return (
    <Group noWrap pt="02" spacing="03">
      <Group noWrap spacing="01" w="200px" miw="200px" align="center">
        <Text w="200px" variant="sm" fw={500} c="gray.9" my="auto">
          {criterion.title}
        </Text>
      </Group>
      <Group
        spacing="04"
        w="100%"
        noWrap
        sx={{ alignItems: 'stretch', flexGrow: 2 }}
      >
        <AnswerLeftBorder variant={answerType} />
        <AnswerText
          text={criterion.manualAnswer || criterion.llmAnswer}
          placeholder={t('criteriaCard.noAnswer')}
        />
      </Group>
    </Group>
  );
}

function Header({ handlePrint }: { handlePrint: () => void }) {
  const { t } = useTranslation();
  return (
    <Group
      noWrap
      position="apart"
      h={NAVBAR_HEIGHT}
      px="04"
      py="02"
      bg="white"
      sx={theme => ({
        boxShadow: theme.shadows.xs,
        borderBottom: `1px solid ${theme.colors.gray[2]}`,
      })}
    >
      <Image src={TengoLogoWithText} alt="Tengo logo" height={26} width={100} />
      <Button
        leftIcon={<IconDownload />}
        onClick={handlePrint}
        sx={{
          '@media print': {
            display: 'none',
          },
        }}
      >
        {t('download')}
      </Button>
    </Group>
  );
}

function Footer() {
  return (
    <Group
      noWrap
      pos="sticky"
      bottom="20px"
      h="80px"
      spacing={0}
      position="apart"
      m="04"
      sx={theme => ({
        background: theme.colors.primary[1],
        boxShadow: theme.shadows.lg,
        padding: `${theme.spacing['05']} ${theme.spacing['04']}`,
        borderRadius: theme.radius.md,
        zIndex: 1,
        overflow: 'hidden',
        // Override for printing:
        '@media print': {
          position: 'relative',
          bottom: 'auto',
        },
      })}
    >
      <RectangleBackground style={{ top: '80px', left: '200px' }} />
      <Group noWrap spacing="02">
        <Image
          src={TengoLogoWithoutText}
          alt="Tengo logo"
          height={45}
          width={60}
          sx={{ zIndex: 3 }}
        />

        <Title order={5} sx={{ zIndex: 3 }}>
          <Trans
            ns="shared"
            i18nKey="footer.content"
            components={{ highlight: <Highlight /> }}
          />
        </Title>
      </Group>
      <RectangleBackground
        style={{
          top: '-115px',
          right: '-20px',
          width: 168,
          height: 168,
        }}
      />
      <Button sx={{ zIndex: 3 }} component="a" href="https://www.tengo.cc">
        Accéder à Tengo
      </Button>
    </Group>
  );
}

function RectangleBackground({ style }: { style: CSSObject }) {
  return (
    <Box
      sx={theme => ({
        position: 'absolute',
        width: 268,
        height: 268,
        transform: 'rotate(45deg)',
        background: theme.colors.primary[2],
        borderRadius: theme.radius.md,
        zIndex: 2,
        ...style,
      })}
    />
  );
}

function paperBorder(theme: MantineTheme) {
  return { border: `1px solid ${theme.colors.gray[1]}` };
}

function Highlight({ children }: { children?: React.ReactNode }) {
  return (
    <Text component="span" sx={theme => ({ color: theme.colors.primary[7] })}>
      {children}
    </Text>
  );
}
